<script lang="ts">
import type { InputEmits, InputSlots, InputProps as UInputProps } from '#ui/types';
import type { AcceptableValue } from '#ui/types/utils';

export interface SlugifyOptions {
	lower?: boolean;
	strict?: boolean;
	replacement?: string;
	remove?: RegExp;
	trim?: boolean;
}

export type SlugifyPreset = 'db-safe' | 'url-path-safe' | 'api-safe';

export interface InputProps<T extends AcceptableValue = AcceptableValue> extends UInputProps<T> {
	modelValue?: T;
	transform?: (value: T | undefined) => (T | undefined);
	slugify?: boolean | SlugifyOptions | SlugifyPreset;
	/**
	 * Take full width
	 */
	block?: boolean;
	copy?: boolean;
}

const URL_PATH_SAFE_SLUGIFY_OPTIONS: SlugifyOptions = {
	remove: /[^\w\s$*+~()'"!:@/]+/g,
	lower: true,
	trim: false,
};
</script>

<script setup lang="ts" generic="T extends AcceptableValue = AcceptableValue">
import Input from '@nuxt/ui/components/Input.vue';
import { controlledComputed, reactiveOmit, syncRefs } from '@vueuse/core';
import { useForwardPropsEmits } from 'reka-ui';
import slugifyFn from 'slugify';
import { computed, useTemplateRef } from 'vue';
import { isNil } from '#layers/base';
import { PASSWORD_MANAGER_IGNORE_ATTRS } from '../constants/attributes';
import { toApiSafeValue } from '../utils/api-safe';
import { toDbSafeValue } from '../utils/db-safe';

const props = defineProps<InputProps<T>>();
const emit = defineEmits<InputEmits<T>>();
const slots = defineSlots<InputSlots>();

const writableModelValue = controlledComputed(() => props.modelValue, {
	get() {
		return props.modelValue;
	},
	set(value) {
		if (typeof props.slugify === 'string' && typeof value === 'string') {
			if (props.slugify === 'db-safe') {
				value = toDbSafeValue(value) as T;
			}
			else if (props.slugify === 'api-safe') {
				value = toApiSafeValue(value) as T;
			}
			else if (props.slugify === 'url-path-safe') {
				value = slugifyFn(value, URL_PATH_SAFE_SLUGIFY_OPTIONS) as T;
			}
		}
		else if (props.slugify && value) {
			value = (props.slugify === true
				? slugifyFn(value as string)
				: slugifyFn(value as string, props.slugify as SlugifyOptions)) as T;
		}

		if (props.transform) {
			value = props.transform(value as T | undefined);
		}

		if (props.modelValue === value) return;

		emit('update:modelValue', value as T);
		writableModelValue.trigger();
	},
});

syncRefs(() => props.modelValue, writableModelValue);

const forward = useForwardPropsEmits(reactiveOmit(props, 'class', 'transform', 'slugify'), emit);
const inputRef = useTemplateRef('input');

const passwordManagersIgnoreAttrs = computed(() => {
	// Convince password managers that autocomplete="off" is a good idea
	if (props.autocomplete === 'off') {
		return PASSWORD_MANAGER_IGNORE_ATTRS;
	}

	return {};
});

const copyButtonSize = computed(() => props.size === 'lg' || props.size === 'xl' || props.size == null ? 'md' : props.size);
const copyButtonMarginEnd = computed(() => copyButtonSize.value === 'xs' ? '-me-1' : '-me-1.5');
const showCopyButton = computed(() => props.copy && !isNil(writableModelValue.value));

defineExpose({
	inputRef: computed(() => inputRef.value?.inputRef),
});
</script>

<template>
	<Input
		ref="input"
		v-bind="{ ...forward, ...passwordManagersIgnoreAttrs }"
		v-model="writableModelValue"
		:model-modifiers="modelModifiers ?? {}"
		:spellcheck="$attrs.spellcheck ?? props.slugify === 'db-safe' ? 'false' : undefined"
		:class="[props.class, props.block ? 'w-full' : '']"
	>
		<template v-for="(_, name) in slots" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>
		<template v-if="showCopyButton" #trailing>
			<DCopyButton :value="String(writableModelValue)" :size="copyButtonSize" :class="copyButtonMarginEnd" />
		</template>
	</Input>
</template>
